// @file Store that handles tag inputs in the active draft composer modal (popover anchor, new section input, ...)
import type { ActiveInputTag, PopoverAnchor } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceComposerModalTagInputsStore = defineStore('surfaceComposerModalTagInputs', () => {
  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const sectionChipPosition = ref<PopoverAnchor>({ top: 0, left: 0, width: 0 })
  const singleSelectChipPosition = ref<PopoverAnchor>({ top: 0, left: 0, width: 0 })
  const locationChipPosition = ref<PopoverAnchor>({ top: 0, left: 0, width: 0 })
  const postColorChipPosition = ref<PopoverAnchor>({ top: 0, left: 0, width: 0 })
  const xNewSectionPrompt = ref<boolean>(false)
  const activeInputTag = ref<'section' | 'location' | 'postColor' | 'none' | 'singleSelect'>('none')

  const isShowingTagMenu = computed<boolean>(() => activeInputTag.value !== 'none')
  const xSectionMenu = computed<boolean>(() => activeInputTag.value === 'section')
  const xSingleSelectMenu = computed<boolean>(() => activeInputTag.value === 'singleSelect')
  const xLocationMenu = computed<boolean>(() => activeInputTag.value === 'location')
  const xPostColorMenu = computed<boolean>(() => activeInputTag.value === 'postColor')

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const openPopover = ({ inputType }: { inputType: ActiveInputTag }): void => {
    activeInputTag.value = inputType
  }

  const closePopover = (): void => {
    activeInputTag.value = 'none'
  }

  const updatePopoverAnchorPositions = ({
    sectionChipPosition: newSectionChipPosition,
    singleSelectChipPosition: newSingleSelectChipPosition,
    locationChipPosition: newLocationChipPosition,
    postColorChipPosition: newPostColorChipPosition,
  }: {
    sectionChipPosition: PopoverAnchor
    singleSelectChipPosition: PopoverAnchor
    locationChipPosition: PopoverAnchor
    postColorChipPosition: PopoverAnchor
  }): void => {
    sectionChipPosition.value = {
      top: newSectionChipPosition.top,
      left: newSectionChipPosition.left,
      width: newSectionChipPosition.width,
    }
    singleSelectChipPosition.value = {
      top: newSingleSelectChipPosition.top,
      left: newSingleSelectChipPosition.left,
      width: newSingleSelectChipPosition.width,
    }
    locationChipPosition.value = {
      top: newLocationChipPosition.top,
      left: newLocationChipPosition.left,
      width: newLocationChipPosition.width,
    }
    postColorChipPosition.value = {
      top: newPostColorChipPosition.top,
      left: newPostColorChipPosition.left,
      width: newPostColorChipPosition.width,
    }
  }

  const openNewSectionPrompt = (): void => {
    xNewSectionPrompt.value = true
  }

  const closeNewSectionPrompt = (): void => {
    xNewSectionPrompt.value = false
  }

  return {
    // Getters
    sectionChipPosition,
    singleSelectChipPosition,
    locationChipPosition,
    postColorChipPosition,
    xNewSectionPrompt,
    isShowingTagMenu,
    xSectionMenu,
    xSingleSelectMenu,
    xLocationMenu,
    xPostColorMenu,

    // Actions
    openPopover,
    closePopover,
    updatePopoverAnchorPositions,
    openNewSectionPrompt,
    closeNewSectionPrompt,
  }
})
